/**
 * Single source of truth for the app's grantable sections.
 *
 * `key`  — permission identifier. MUST match the server's SECTION_KEYS and the
 *          per-user `allowed_sections` stored in the DB. Never rename a key
 *          without migrating those grants.
 * `path` — the browser URL for the section. Decoupled from `key` (routing maps
 *          path → PAGE_BY_KEY[key]), so the URL can be made human-readable
 *          independently. The EOM (`dashboard` key → `/eom`) and Month-on-Month
 *          (`eom` key → `/month-on-month`) entries intentionally differ from
 *          their keys for this reason.
 */
export interface AppSection {
  key: string;
  label: string;
  icon: string;
  path: string;
}

// ⚠ EOM naming map (the two entries below are easy to mix up — keys are historical
// and stored in each user's DB `allowed_sections`, so they can't be renamed freely):
//   key `dashboard` → "EOM" tab          → EOMPage          → DB-backed end-of-month dashboard.
//   key `eom`       → "Month on Month" tab → MonthOnMonthPage → DB-backed (eom_snapshot for
//                                            settled months + live DB compute). No Google Sheet:
//                                            the EOM tab was cut over off Sheets on 2026-06-01.
// Nav display order (User Management is appended separately for admins, so it
// always stays last). Reorder these entries to reorder the tab row.
export const SECTIONS: AppSection[] = [
  // [LEAD-ASSIGNMENT 2026-10-13, user] "before stats, add a tab for 'Lead Assignment'". First in the row; the landing page
  // stays Payments (App.tsx prefers it). KEY permanent once granted; the page is a placeholder until its content is built.
  // [LEAD-MANAGEMENT 2026-10-13, user] "make heading lead management here too" — the name only; key + path stay (grants use the key).
  { key: 'lead-assignment', label: 'Lead Management', icon: 'fas fa-people-arrows', path: '/lead-assignment' },
  // [TEAM-SAMEER 2026-10-15, user] "after lead management tab add a tab for 'Team Sameer Analysis'", then "make the name 'Team
  // Sameer Report'": per lead generator of Team Sameer, their Leads_Raw rows between two dates, NAL and Final Count. KEY permanent once granted.
  { key: 'team-sameer-report', label: 'Team Sameer Report', icon: 'fas fa-users-line', path: '/team-sameer-report' },
  // [LGP 2026-10-15, user] "add a new tab after team sameer report which is named as 'Lead Generator Performance'" — the Apps
  // Script dashboard (Leads Count, Monthly Performance, NAL Leads, Team Roster, Bonus Rules) on our database. KEY permanent once
  // granted; the grant also lets a person edit the roster and the bonus rules (user: "admin only and the person having the
  // access for this tab").
  { key: 'lead-generator-performance', label: 'Lead Generator Performance', icon: 'fas fa-chart-line', path: '/lead-generator-performance' },
  // [DT-DAILY 2026-10-16, user] "after lead generator performance tab i want to add a new tab for the dt daily report tab name will be
  // 'DT Daily Report'" — the Apps Script "SEO Daily Leads Assignment Report" on our server. KEY permanent once granted.
  { key: 'dt-daily-report', label: 'DT Daily Report', icon: 'fas fa-calendar-day', path: '/dt-daily-report' },
  { key: 'stats', label: 'Stats', icon: 'fas fa-chart-bar', path: '/stats' },
  { key: 'payments', label: 'Payments', icon: 'fas fa-credit-card', path: '/payments' },
  { key: 'dashboard', label: 'EOM', icon: 'fas fa-th-large', path: '/eom' },
  { key: 'retention-analysis', label: 'Retention Analysis', icon: 'fas fa-chart-line', path: '/retention-analysis' },
  { key: 'delayed-payments', label: 'Delayed Payments', icon: 'fas fa-hourglass-half', path: '/delayed-payments' },
  // "Month on Month" — DB-backed (EOM tab). See the EOM naming map above.
  { key: 'eom', label: 'Month on Month', icon: 'fas fa-calendar-check', path: '/month-on-month' },
  { key: 'refunds', label: 'Refunds', icon: 'fas fa-undo', path: '/refunds' },
  { key: 'disputes', label: 'Disputes', icon: 'fas fa-exclamation-triangle', path: '/disputes' },
  { key: 'performance', label: 'PM Performance', icon: 'fas fa-chart-pie', path: '/performance' },
  // [MY-PERF 2026-09-02] One PM's whole record, first month to the last complete month. The KEY is
  // permanent once granted (stored in users.allowed_sections); the label is free to change.
  { key: 'my-performance', label: 'Performance Review', icon: 'fas fa-id-card', path: '/my-performance' },
  { key: 'inventory', label: 'Inventory', icon: 'fas fa-map', path: '/inventory' },
  { key: 'my-inventory', label: 'My Inventory', icon: 'fas fa-box', path: '/my-inventory' },
  { key: 'inventory-view', label: 'Inventory View', icon: 'fas fa-table', path: '/inventory-view' },
  { key: 'dialing-report', label: 'Dialing Report', icon: 'fas fa-phone-volume', path: '/dialing-report' },
  { key: 'new-sales', label: 'New Sales', icon: 'fas fa-cart-plus', path: '/new-sales' },
  // [SALES-PIPELINE 2026-10-07, user] Right before My Sales (user's placement). KEY permanent once granted.
  { key: 'sales-pipeline', label: 'Sales Pipeline', icon: 'fas fa-table-columns', path: '/sales-pipeline' },
  { key: 'my-sales', label: 'My Sales', icon: 'fas fa-receipt', path: '/my-sales' },
  { key: 'sales-scoreboard', label: 'Sales Scoreboard', icon: 'fas fa-trophy', path: '/sales-scoreboard' },
  { key: 'process-analysis', label: 'Process Analysis', icon: 'fas fa-diagram-project', path: '/process-analysis' },
  // [PM-RECS 2026-08-25] Sits directly after Process Analysis (user's placement). The KEY is
  // permanent (stored in users.allowed_sections); the LABEL is free to change here alone.
  { key: 'pm-recommendations', label: 'PM Recommendations', icon: 'fas fa-lightbulb', path: '/pm-recommendations' },
  { key: 'application-logs', label: 'Application Logs', icon: 'fas fa-clipboard-list', path: '/application-logs' },
];

export const SECTION_KEYS = SECTIONS.map((s) => s.key);

/* [TL-REPORT-ONLY 2026-10-19, user] "now i want to give the access of the lead management to these mails (anand.singh, ankit.pal,
   manjeet.singh, sunil.kumar @emailteam.co) but in the lead management page they will only see the lead generator report and nothing
   else" + "given the access to all TL's … just wanted to make sure that they will only see this lead generator report". The
   Leads-type rules the dock and the pages share, in one place. */
type AccessUser = { directory_role?: string | null; is_admin?: boolean; allowed_sections?: string[] | null; lead_report_only?: boolean } | null | undefined;
/** The Leads type in User Management (not an admin): Shivam, Harshit and the team leaders. */
export const isLeadsType = (u: AccessUser): boolean => !!u && u.directory_role === 'leads' && !u.is_admin;
/** One of the FOUR team leaders (user: "make the thing fix for these 4 members … there are other person also who has the access for
 *  the team sameer and also for this so they should not work like this"): the server names them (services/leadReportOnly.ts → /api/auth/me
 *  `lead_report_only`) — in Lead Management they get the Lead Generator Report only, and the server refuses the rest for them too. */
export const isLeadReportOnly = (u: AccessUser): boolean => !!u && !u.is_admin && !!u.lead_report_only;
/** [LEADS-NO-DOCK] No dock (and no Go-to-Top) for the Leads type with ONE tab (Shivam, Harshit). [TL-REPORT-ONLY] A TL has two
 *  (Lead Management + Team Sameer Report) and gets the dock to move between them. */
export const leadsNoDock = (u: AccessUser): boolean => isLeadsType(u)
  && (u?.allowed_sections || []).filter((k) => SECTION_KEYS.includes(k)).length <= 1;

/**
 * A grantable section as shown in User Management — a DISPLAY GROUP. Refunds +
 * Disputes collapse into ONE "Refunds/Disputes" group (granting it grants both
 * underlying permission keys) so the grant UI matches the merged nav tab. Every
 * other section is its own group of one. `keys` are the real permission keys the
 * group writes to a user's `allowed_sections`.
 */
export interface SectionGroup {
  id: string;
  label: string;
  icon: string;
  keys: string[];
}

export const SECTION_GROUPS: SectionGroup[] = (() => {
  const groups: SectionGroup[] = [];
  let mergedInserted = false;
  for (const s of SECTIONS) {
    if (s.key === 'refunds' || s.key === 'disputes') {
      if (!mergedInserted) {
        groups.push({ id: 'refunds-disputes', label: 'Refunds/Disputes', icon: 'fas fa-undo', keys: ['refunds', 'disputes'] });
        mergedInserted = true;
      }
      continue;
    }
    groups.push({ id: s.key, label: s.label, icon: s.icon, keys: [s.key] });
  }
  return groups;
})();

/** Admin-only section — never grantable via checkbox; admins get it automatically. */
export const USER_MANAGEMENT: AppSection = {
  key: 'user-management',
  label: 'User Management',
  icon: 'fas fa-users-cog',
  path: '/user-management',
};

/**
 * [PM-LOGIN 2026-08-11] PM Singapore — sits directly AFTER User Management in the pill row.
 *
 * Deliberately NOT in SECTIONS: everything in that array is a grantable checkbox in User
 * Management, and this one is not grantable. It is visible ONLY to the shared username/password
 * account, whose allowed_sections is exactly ['pm-singapore'] — which is why the key still has to
 * be a real permission key (the server enforces it through the same allowed_sections machinery as
 * every other tab), even though no UI ever grants it.
 *
 * [PM-SG 2026-08-11] Not admins either. The competition board belongs to the credentials sign-in
 * alone; for a Google user this section does not exist.
 */
export const PM_SINGAPORE: AppSection = {
  key: 'pm-singapore',
  label: 'PM Singapore',
  icon: 'fas fa-city',
  path: '/pm-singapore',
};

/**
 * [SALES-ROW 2026-09-30, user] The Team ROW Sales board. NOT in SECTIONS (not grantable in User
 * Management) and not in the dock: it opens from the header's "ROW Dashboard" button.
 * [ROW-BUTTON 2026-09-30, user] Who may open it is decided on the server (/me `can_sales_row`):
 * admins, Team ROW - Sales and Vishal Suryavanshi ([ROW-ACCESS-TEAM 2026-10-01, user]; admins only for a day,
 * [ROW-ADMIN-ONLY]). The first version used a shared admin + password
 * sign-in; the user replaced that with the button the same day. /api/sales-row checks the same rule.
 */
export const SALES_ROW: AppSection = {
  key: 'sales-row',
  label: 'Sales ROW',
  icon: 'fas fa-globe',
  path: '/sales-row',
};

/**
 * [DUBAI 2026-10-19, user] The GITEX GLOBAL Dubai contest TV board. Same shape as PM_SINGAPORE: NOT in SECTIONS (not
 * grantable), no pill, no button — only the board's own sign-in (admin + its password) carries the `dubai-contest` grant,
 * and that account opens straight onto it. Not admins either (user: "no button on the application").
 */
export const DUBAI_CONTEST: AppSection = {
  key: 'dubai-contest',
  label: 'Dubai Contest',
  icon: 'fas fa-plane',
  path: '/dubai-contest',
};

/** Admin-only audit trail — sits directly below User Management. Admins only. */
export const APPLICATION_LOGS: AppSection = {
  key: 'application-logs',
  label: 'Application Logs',
  icon: 'fas fa-clipboard-list',
  path: '/application-logs',
};

/**
 * Personal inventory view — a reflection of the Inventory section filtered to
 * the current user (defaults the PM dropdown to Shreyansh Gupta). Not grantable
 * and not admin-only: every logged-in user sees it, pinned to the bottom of the
 * sidebar below the admin items.
 */
export const MY_INVENTORY: AppSection = {
  key: 'my-inventory',
  label: 'My Inventory',
  icon: 'fas fa-box',
  path: '/my-inventory',
};

/** A rendered nav pill. `matchPaths` lists every route that should light up this
 *  pill as active — the merged Refunds/Disputes pill matches BOTH routes so the
 *  active highlight doesn't jump when you switch views inside the page. */
export interface NavItem {
  id: string;
  icon: string;
  text: string;
  path: string;
  matchPaths: string[];
}

/**
 * Build the ordered nav-pill list for a user — shared by the bottom dock and the
 * sidebar so the two never drift apart.
 *
 * Refunds + Disputes collapse into a SINGLE "Refunds/Disputes" pill (the page
 * itself carries a Refunds | Disputes pill — see SectionSwitcher — to slide between them),
 * which keeps the tab row from getting crowded. The merge only happens when the
 * user can actually see both; if only one is granted it stays a normal single
 * pill. `refunds` and `disputes` remain independent permission keys/routes — only
 * their nav presentation is merged. User Management is appended last for admins.
 */
export function buildNavItems(isAdmin: boolean, allowed: Set<string>): NavItem[] {
  const visible = isAdmin ? SECTIONS : SECTIONS.filter((s) => allowed.has(s.key));
  const base: NavItem[] = visible.map((s) => ({
    id: s.key, icon: s.icon, text: s.label, path: s.path, matchPaths: [s.path],
  }));

  const hasRefunds = base.some((i) => i.id === 'refunds');
  const hasDisputes = base.some((i) => i.id === 'disputes');
  let items = base;
  if (hasRefunds && hasDisputes) {
    const merged: NavItem = {
      id: 'refunds-disputes',
      icon: 'fas fa-undo',
      text: 'Refunds/Disputes',
      path: '/refunds',
      matchPaths: ['/refunds', '/disputes'],
    };
    // Swap the refunds pill for the merged one (keeping its slot) and drop disputes.
    items = base
      .filter((i) => i.id !== 'disputes')
      .map((i) => (i.id === 'refunds' ? merged : i));
  }

  if (isAdmin) {
    items = [...items, {
      id: USER_MANAGEMENT.key,
      icon: USER_MANAGEMENT.icon,
      text: USER_MANAGEMENT.label,
      path: USER_MANAGEMENT.path,
      matchPaths: [USER_MANAGEMENT.path],
    }];
  }

  /* [PM-LOGIN 2026-08-11] Appended last in the pill row.
     [PM-SG 2026-08-11] ONE way in and no second: the explicit `pm-singapore` grant, which only
     the shared credentials account carries. Admins deliberately do NOT see this pill — the
     user's call, so Google sign-in is left exactly as it was before this feature existed. A
     Google user who types the URL renders nothing (the route is gated on the same condition in
     App.tsx) and their API calls 403 (same section key, middleware/sectionGuard). */
  if (allowed.has(PM_SINGAPORE.key)) {
    items = [...items, {
      id: PM_SINGAPORE.key,
      icon: PM_SINGAPORE.icon,
      text: PM_SINGAPORE.label,
      path: PM_SINGAPORE.path,
      matchPaths: [PM_SINGAPORE.path],
    }];
  }
  return items;
}
